"use client"

import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { useTranslations } from 'next-intl'

const Content = () => {
    const t = useTranslations('IAPolitica');

    // Refs for scroll animations
    const mainRef = useRef(null)
    const featuresRef = useRef(null)
    const aiAllyRef = useRef(null)
    const controlRef = useRef(null)
    const benefitsRef = useRef(null)

    // Check if elements are in view
    const mainInView = useInView(mainRef, { once: true, margin: "-100px" })
    const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" })
    const aiAllyInView = useInView(aiAllyRef, { once: true, margin: "-100px" })
    const controlInView = useInView(controlRef, { once: true, margin: "-100px" })
    const benefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" })

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.5,
                delayChildren: 0.3
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8
            }
        }
    }

    const cardVariants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    // Individual card variants with specific delays
    const card1Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.2
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const card2Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.7
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const card3Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 1.2
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const slideInVariants = {
        hidden: { opacity: 0, x: -50 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.7
            }
        }
    }

    const slideInRightVariants = {
        hidden: { opacity: 0, x: 50 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.7
            }
        }
    }

    // Benefits card variants with individual delays
    const benefit1Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.1
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const benefit2Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.3
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const benefit3Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.5
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const benefit4Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.7
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const benefit5Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.9
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const benefit6Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 1.1
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    return (
        <div className="">
            <motion.div
                ref={mainRef}
                initial="hidden"
                animate={mainInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.h3
                    className="text-2xl font-normal text-red-600 dark:text-red-400 mb-8 italic -mt-8"
                    variants={itemVariants}
                >
                    {t('intro')}
                    <br /><br />{t('intro_continuation')}
                </motion.h3>

                <motion.h3
                    className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-6"
                    variants={itemVariants}
                >
                    {t('three_areas')}
                </motion.h3>
            </motion.div>

            <motion.div
                ref={featuresRef}
                className="space-y-8"
                initial="hidden"
                animate={featuresInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                {/* Comunicación Personalizada */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card1Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">{t('personalized_communication.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('personalized_communication.description')}
                    </p>
                </motion.div>

                {/* Análisis y Predicción */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card2Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">{t('analysis_prediction.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('analysis_prediction.description')}
                    </p>
                </motion.div>

                {/* Defensa y Gestión de Crisis */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card3Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">{t('defense_crisis.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('defense_crisis.description')}
                    </p>
                </motion.div>
            </motion.div>

            {/* New section about AI as campaign ally */}
            <motion.div
                ref={aiAllyRef}
                className="mt-12 space-y-8"
                initial="hidden"
                animate={aiAllyInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.h3
                    className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-6"
                    variants={itemVariants}
                >
                    {t('ai_ally_title')}
                </motion.h3>

                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('ai_ally_description')}
                    </p>
                </motion.div>

                <motion.h4
                    className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4"
                    variants={itemVariants}
                >
                    {t('ai_team_integration')}
                </motion.h4>

                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('ai_team_description')}
                    </p>
                </motion.div>

                <motion.h4
                    className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4"
                    variants={itemVariants}
                >
                    {t('gps_electoral')}
                </motion.h4>

                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('gps_description')}
                    </p>
                </motion.div>

                <motion.h4
                    className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4"
                    variants={itemVariants}
                >
                    {t('digital_bodyguard')}
                </motion.h4>

                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('bodyguard_description')}
                    </p>
                </motion.div>
            </motion.div>

            {/* New section: El control es siempre suyo */}
            <motion.div
                ref={controlRef}
                className="mt-12 space-y-8"
                initial="hidden"
                animate={controlInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.h3
                    className="text-2xl font-bold text-red-600 dark:text-red-400 mb-8 text-center italic"
                    variants={itemVariants}
                >
                    {t('control_yours')}
                </motion.h3>

                <motion.div
                    className="grid lg:grid-cols-2 gap-8"
                    variants={containerVariants}
                >
                    {/* Left Column */}
                    <motion.div
                        className="space-y-6"
                        variants={containerVariants}
                    >
                        <motion.h4
                            className="text-xl font-bold text-red-600 dark:text-red-400 mb-4"
                            variants={slideInVariants}
                        >
                            {t('strategy_generation')}
                        </motion.h4>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                            variants={cardVariants}
                            whileHover="hover"
                        >
                            <h5 className="text-lg font-bold text-red-600 dark:text-red-400 mb-3">{t('repartee')}</h5>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                {t('repartee_description')}
                            </p>
                        </motion.div>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                            variants={cardVariants}
                            whileHover="hover"
                        >
                            <h5 className="text-lg font-bold text-red-600 dark:text-red-400 mb-3">{t('time_saving')}</h5>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                {t('time_saving_description')}
                            </p>
                        </motion.div>
                    </motion.div>

                    {/* Right Column */}
                    <motion.div
                        className="space-y-6"
                        variants={containerVariants}
                    >
                        <motion.h4
                            className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4"
                            variants={slideInRightVariants}
                        >
                            {t('ai_tool_setup')}
                        </motion.h4>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                            variants={cardVariants}
                            whileHover="hover"
                        >
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                {t('ai_tool_description')}
                            </p>
                        </motion.div>
                        <motion.div
                            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                            variants={cardVariants}
                            whileHover="hover"
                        >
                            <h5 className="text-lg font-bold text-red-600 dark:text-red-400 mb-3">{t('different_roles')}</h5>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                {t('roles_description')}
                            </p>
                        </motion.div>
                    </motion.div>
                </motion.div>
                <motion.div
                    ref={benefitsRef}
                    initial="hidden"
                    animate={benefitsInView ? "visible" : "hidden"}
                    variants={containerVariants}
                >
                    <motion.h5
                        className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-4"
                        variants={itemVariants}
                    >
                        {t('benefits_title')}
                    </motion.h5>

                    <motion.div
                        className="space-y-4"
                        variants={containerVariants}
                    >
                        <motion.div
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md dark:shadow-lg"
                            variants={benefit1Variants}
                            whileHover="hover"
                        >
                            <h6 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{t('benefits.coherence.title')}</h6>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{t('benefits.coherence.description')}</p>
                        </motion.div>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md dark:shadow-lg"
                            variants={benefit2Variants}
                            whileHover="hover"
                        >
                            <h6 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{t('benefits.speed.title')}</h6>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{t('benefits.speed.description')}</p>
                        </motion.div>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md dark:shadow-lg"
                            variants={benefit3Variants}
                            whileHover="hover"
                        >
                            <h6 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{t('benefits.time_presence.title')}</h6>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{t('benefits.time_presence.description')}</p>
                        </motion.div>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md dark:shadow-lg"
                            variants={benefit4Variants}
                            whileHover="hover"
                        >
                            <h6 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{t('benefits.efficiency.title')}</h6>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{t('benefits.efficiency.description')}</p>
                        </motion.div>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md dark:shadow-lg"
                            variants={benefit5Variants}
                            whileHover="hover"
                        >
                            <h6 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{t('benefits.presence.title')}</h6>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{t('benefits.presence.description')}</p>
                        </motion.div>

                        <motion.div
                            className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md dark:shadow-lg"
                            variants={benefit6Variants}
                            whileHover="hover"
                        >
                            <h6 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{t('benefits.optimization.title')}</h6>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{t('benefits.optimization.description')}</p>
                        </motion.div>
                    </motion.div>
                </motion.div>

            </motion.div>

        </div>
    )
}

export default Content
